/**
 * Theme-Aware Color Generation Utility
 * 
 * Provides consistent, theme-integrated color generation for UI components.
 * Replaces hardcoded color arrays with dynamic theme-based color palettes.
 * 
 * Design Principles:
 * - Single source of truth for color generation
 * - Theme-aware and responsive to user preferences  
 * - Consistent color distribution across components
 * - Accessibility-compliant contrast ratios
 */

import { Theme } from '@mui/material/styles';
import React from 'react';

interface ColorGenerator {
  /** Generate color by index (for sequential items like timeline events) */
  byIndex: (index: number) => string;
  
  /** Generate color by string hash (for categories, consistent across renders) */
  byHash: (str: string) => string;
  
  /** Get accessible contrast text color for any background */
  getContrastText: (backgroundColor: string) => string;
  
  /** Get all available colors in the palette */
  getPalette: () => string[];
}

/**
 * Hash function for consistent color assignment
 * Maintains compatibility with existing getCategoryColor behavior
 */
const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    const chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

/**
 * Create a theme-aware color generator
 * 
 * @param theme - MUI Theme object
 * @returns ColorGenerator with theme-integrated methods
 */
export const createThemeColorGenerator = (theme: Theme): ColorGenerator => {
  // Build color palette from theme - maintains visual hierarchy
  const colorPalette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success?.main || '#4caf50',
    theme.palette.info?.main || '#2196f3', 
    theme.palette.warning?.main || '#ff9800',
    theme.palette.primary.light || theme.palette.primary.main,
    theme.palette.secondary.light || theme.palette.secondary.main,
    theme.palette.primary.dark || theme.palette.primary.main,
    theme.palette.secondary.dark || theme.palette.secondary.main,
    theme.palette.error?.main || '#f44336',
  ];

  return {
    byIndex: (index: number) => {
      return colorPalette[index % colorPalette.length];
    },
    
    byHash: (str: string) => {
      if (!str) return colorPalette[0];
      
      try {
        const hash = hashCode(str);
        const index = Math.abs(hash) % colorPalette.length;
        return colorPalette[index];
      } catch (error) {
        // Fallback to primary color on any error
        console.warn('[ThemeColorGenerator] Error generating color for:', str, error);
        return theme.palette.primary.main;
      }
    },
    
    getContrastText: (backgroundColor: string) => {
      return theme.palette.getContrastText(backgroundColor);
    },
    
    getPalette: () => [...colorPalette]
  };
};

/**
 * React hook for using theme-aware colors in components
 * Memoized to prevent unnecessary recalculations
 */
export const useThemeColors = (theme: Theme): ColorGenerator => {
  return React.useMemo(() => createThemeColorGenerator(theme), [theme]);
};

/**
 * Backwards-compatible function that maintains existing API
 * while using theme-aware colors internally
 * 
 * @deprecated Use createThemeColorGenerator or useThemeColors instead
 */
export const getCategoryColorThemeAware = (category: string, theme: Theme): string => {
  const generator = createThemeColorGenerator(theme);
  return generator.byHash(category);
};